import {Fragment} from "react";
import {toCentsFromMoney} from "../../../../../lib/Money.ts";
import {isDrillable, rowLabel} from "../../utils";
import {ReportRowKind, type ViewRow} from "../../types";
import {AmountCell, SlimAmountCell} from "./AmountCells.tsx";
import {ComparisonCells} from "./ComparisonCells.tsx";
import {RowBadge} from "./RowBadge.tsx";
import {
    COL_SEP_L,
    COL_SEP_R,
    isBalanceCheckImbalanced,
    isBlankedHeader,
    isBucketBlankForSection,
    levelPadding,
    rowClassName,
    stickyAccountCellBg,
    type AmountGroup,
} from "./tableStyles.ts";
import type {StatementSection} from "./types.ts";
import type {LegalEntityType, SbsSection} from "../../../../../generated/enums.ts";

interface StatementTableRowProps {
    row: ViewRow;
    sectionKind: SbsSection;
    groups: AmountGroup[];
    isSlim: boolean;
    hasComparison: boolean;
    legalEntityType: LegalEntityType;
    balanceDiffCents: number;
    onToggleNode: (id: string) => void;
    onLeafClick: (row: ViewRow) => void;
    onKpiClick: (row: ViewRow) => void;
}

export function StatementTableRow({
    row,
    sectionKind,
    groups,
    isSlim,
    hasComparison,
    legalEntityType,
    balanceDiffCents,
    onToggleNode,
    onLeafClick,
    onKpiClick,
}: StatementTableRowProps) {
    const label = rowLabel(row, legalEntityType);
    const code = row.account?.code;
    const expandable = row.kind === ReportRowKind.Group;
    const clickableKpi =
        row.kind === ReportRowKind.Kpi || row.kind === ReportRowKind.BalanceCheck;
    const drillable = isDrillable(row);
    const isImbalanced = isBalanceCheckImbalanced(row, balanceDiffCents);
    const blankAmounts = isBlankedHeader(row);

    const onRowActivate = () => {
        if (expandable) onToggleNode(row.id);
        else if (clickableKpi) onKpiClick(row);
        else if (drillable) onLeafClick(row);
    };

    const cursor = expandable || clickableKpi || drillable ? "cursor-pointer" : "";

    return (
        <tr
            className={`group ${rowClassName(row, isImbalanced)} ${cursor}`}
            onClick={onRowActivate}
        >
            <td
                className={`sticky left-0 z-20 px-3 py-2 ${COL_SEP_R} ${stickyAccountCellBg(row, isImbalanced)} ${levelPadding(row.level)}`}
            >
                <div className="flex items-center justify-between gap-3 min-w-0">
                    <div className="flex items-center min-w-0">
                        {expandable && (
                            <span className="mr-1.5 w-3 text-[11px] text-gray-500">
                                {row.expanded ? "▾" : "▸"}
                            </span>
                        )}
                        <div className="min-w-0">
                            <div className="flex items-center flex-wrap">
                                {code && (
                                    <span className="mr-1.5 text-[11px] font-mono text-gray-500">
                                        {code}
                                    </span>
                                )}
                                <span className="truncate">{label}</span>
                                <RowBadge row={row} />
                                {drillable && (
                                    <span className="ml-2 hidden items-center gap-0.5 text-[9px] font-medium text-primary group-hover:inline-flex">
                                        Bronnen ›
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </td>
            {isSlim ? (
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {blankAmounts ? null : <SlimAmountCell row={row} />}
                </td>
            ) : (
                groups.map((group, index) => {
                    const blankBucket =
                        blankAmounts || isBucketBlankForSection(sectionKind, group.bucket);
                    return (
                        <Fragment key={group.bucket}>
                            <td
                                className={`px-3 py-2 text-right tabular-nums ${
                                    index === 0 ? "" : COL_SEP_L
                                }`}
                            >
                                {blankBucket ? null : (
                                    <AmountCell
                                        cents={toCentsFromMoney(
                                            row.amounts[group.bucket].debit_amount,
                                        )}
                                    />
                                )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                                {blankBucket ? null : (
                                    <AmountCell
                                        cents={toCentsFromMoney(
                                            row.amounts[group.bucket].credit_amount,
                                        )}
                                    />
                                )}
                            </td>
                        </Fragment>
                    );
                })
            )}
            {hasComparison && (
                <ComparisonCells row={row} sectionKind={sectionKind} isSlim={isSlim} />
            )}
        </tr>
    );
}

interface SectionRowsProps {
    section: StatementSection;
    groups: AmountGroup[];
    isSlim: boolean;
    hasComparison: boolean;
    legalEntityType: LegalEntityType;
    sectionColSpan: number;
    balanceDiffCents: number;
    onToggleNode: (id: string) => void;
    onLeafClick: (row: ViewRow) => void;
    onKpiClick: (row: ViewRow) => void;
}

export function SectionRows({
    section,
    groups,
    isSlim,
    hasComparison,
    legalEntityType,
    sectionColSpan,
    balanceDiffCents,
    onToggleNode,
    onLeafClick,
    onKpiClick,
}: SectionRowsProps) {
    return (
        <>
            <tr className="text-gray-700 [&>*]:[border-top:1px_solid_#d1d5db] [&>*]:[border-bottom:1px_solid_#d1d5db]">
                <td
                    className={`sticky left-0 z-20 px-3 py-2 text-[12.5px] font-bold uppercase tracking-[0.07em] bg-gray-100 ${COL_SEP_R}`}
                >
                    {section.title}
                </td>
                {sectionColSpan > 1 && (
                    <td
                        colSpan={sectionColSpan - 1}
                        className="bg-gray-100"
                        aria-hidden="true"
                    />
                )}
            </tr>
            {section.rows.map((row) => (
                <StatementTableRow
                    key={row.id}
                    row={row}
                    sectionKind={section.kind}
                    groups={groups}
                    isSlim={isSlim}
                    hasComparison={hasComparison}
                    legalEntityType={legalEntityType}
                    balanceDiffCents={balanceDiffCents}
                    onToggleNode={onToggleNode}
                    onLeafClick={onLeafClick}
                    onKpiClick={onKpiClick}
                />
            ))}
        </>
    );
}
